import { useEffect, useState, type ReactNode } from 'react'
import styles from './AuthPage.module.css'

interface AuthPageProps {
  title: string
  subtitle: string
  children: ReactNode
}

/**
 * Разговор, который проигрывается на экране входа.
 *
 * Настоящий: так агент и звучит на холодном звонке. Здесь есть всё, что
 * отличает его от автоответчика, — перебивание на полуслове, ответ за
 * восемь десятых секунды, отработка отказа. Три буллета с обещаниями
 * пишет кто угодно.
 */
const CONVERSATION = [
  { role: 'agent', text: 'Здравствуйте! Меня зовут Илья, компания Синта. Вам удобно сейчас говорить?', latency: '0.7 с' },
  { role: 'human', text: 'Да, только быстро' },
  {
    role: 'agent',
    text: 'Понял, две минуты. Мы делаем голосовых агентов для обзвона базы —',
    tail: 'они сами звонят, ведут диалог и передают тёплых клиентов менеджеру.',
    cut: true,
    latency: '0.8 с',
  },
  { role: 'human', text: 'У нас уже есть колл-центр' },
  {
    role: 'agent',
    text: 'Тогда вы точно знаете, сколько стоит первый контакт. Мы обычно закрываем именно его — а живые менеджеры получают уже подогретых. Показать на вашей базе?',
    latency: '0.9 с',
  },
  { role: 'human', text: 'Ну давайте, на следующей неделе' },
] as const

export function AuthPage({ title, subtitle, children }: AuthPageProps) {
  const shown = useConversation(CONVERSATION.length)

  return (
    <div className={styles.page}>
      <div className={styles.formSide}>
        <div className={styles.formBox}>
          <div className={styles.logo}>
            <span className={styles.mark}>
              <LogoIcon />
            </span>
            <span>
              синта<span className={styles.logoSoft}>.бот</span>
            </span>
          </div>

          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>

          {children}

          <p className={styles.note}>
            Минута на регистрацию, дальше — разговор с настройщиком. Данные карты не
            нужны: тестовые звонки из кабинета бесплатны.
          </p>
        </div>
      </div>

      <aside className={styles.stage}>
        <p className={styles.pitch}>
          Так он <span className={styles.accentWord}>говорит</span>
        </p>

        <div className={styles.conversation}>
          {CONVERSATION.slice(0, shown).map((turn, i) => (
            <div
              key={i}
              className={`${styles.turn} ${turn.role === 'human' ? styles.turnHuman : ''}`}
            >
              {turn.role === 'agent' && <span className={styles.who} />}

              <div className={`${styles.bubble} ${turn.role === 'human' ? styles.human : ''}`}>
                {turn.text}
                {'tail' in turn && turn.tail && <span className={styles.tail}> {turn.tail}</span>}
                {'cut' in turn && turn.cut && (
                  <span className={styles.cut}>
                    <CutIcon />
                    перебили
                  </span>
                )}
                {'latency' in turn && turn.latency && (
                  <span className={styles.latency}>ответил через {turn.latency}</span>
                )}
              </div>
            </div>
          ))}

          {shown < CONVERSATION.length && (
            <div className={styles.turn}>
              <span className={styles.who} />
              <div className={styles.bubble}>
                <span className={styles.typing}>
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.caption}>
          <span className={styles.captionItem}>
            <span className={styles.captionDot} />
            слышит перебивание и уступает
          </span>
          <span className={styles.captionItem}>
            <span className={styles.captionDot} />
            отвечает меньше чем за секунду
          </span>
        </div>
      </aside>
    </div>
  )
}

/**
 * Показывает реплики по одной.
 *
 * Пауза перед репликой агента длиннее: он должен «подумать», иначе
 * разговор выглядит заранее написанным — чем он, конечно, и является,
 * но выдавать это незачем.
 */
function useConversation(total: number) {
  const [shown, setShown] = useState(0)

  useEffect(() => {
    if (shown >= total) return

    const delay = shown === 0 ? 400 : CONVERSATION[shown]?.role === 'agent' ? 1400 : 900
    const id = window.setTimeout(() => setShown((n) => n + 1), delay)
    return () => window.clearTimeout(id)
  }, [shown, total])

  return shown
}

function LogoIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path
        d="M2 7.5H3.2M5.1 4.3V10.7M7.5 1.9V13.1M9.9 4.3V10.7M12.8 7.5H14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CutIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M6 1.5V10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M3 4L6 1.5L9 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
